import type { ResponseProps } from 'store/api/api.types';
import { apiSlice } from 'store/api/apiSlice';
import { fetchPaginatedData } from 'utils/services';

import type {
  ApiEnvelope,
  MutateSegmentPayload,
  RetrySegmentSyncPayload,
  SaveSegmentPayload,
  SegmentListInfo,
  SegmentStatus,
  ShopSegment,
  TraitRegistryEntry,
  UpdateSegmentPayload
} from './shopSegments.types';

interface GetSegmentsParams {
  limit?: number;
  pageNo?: number;
  status?: SegmentStatus;
}

const shopSegmentsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createShopSegment: builder.mutation<ShopSegment, SaveSegmentPayload>({
      invalidatesTags: ['ShopSegment'],
      query: (body) => ({ body, method: 'POST', url: '/admin/shop/segments' }),
      transformResponse: (response: ApiEnvelope<ShopSegment>) => response.data
    }),
    deleteShopSegment: builder.mutation<ShopSegment, MutateSegmentPayload>({
      invalidatesTags: ['ShopSegment'],
      query: ({ id, version }) => ({
        method: 'DELETE',
        params: { version },
        url: `/admin/shop/segments/${id}`
      }),
      transformResponse: (response: ApiEnvelope<ShopSegment>) => response.data
    }),
    getShopSegmentAttributes: builder.query<TraitRegistryEntry[], void>({
      query: () => '/admin/shop/segments/meta/attributes',
      transformResponse: (response: ApiEnvelope<TraitRegistryEntry[]>) => response.data
    }),
    getShopSegments: builder.query<
      { data: ShopSegment[]; info: SegmentListInfo },
      GetSegmentsParams | void
    >({
      providesTags: (result) =>
        result
          ? [
              'ShopSegment',
              ...result.data.map(({ _id }) => ({ id: _id, type: 'ShopSegment' as const }))
            ]
          : ['ShopSegment'],
      // The segments table and the builder's audience pickers render the full
      // list, so follow `info` across pages instead of silently truncating at
      // the first `limit` rows.
      async queryFn(arg, api, extraOptions, baseQuery) {
        const { limit = 100, pageNo = 0, status } = arg ?? {};
        const result = await fetchPaginatedData<
          ShopSegment,
          ResponseProps<ShopSegment[], SegmentListInfo>
        >({
          api,
          baseQuery,
          createEmptyResponse: () => ({
            data: [],
            errorCode: 0,
            info: { nextPageAvailable: false, remainingCount: 0, totalCount: 0 },
            message: ''
          }),
          extraOptions,
          limit,
          pageNo,
          params: status ? { status } : undefined,
          shouldFetchAllPages: true,
          url: '/admin/shop/segments'
        });

        if ('error' in result) return { error: result.error };

        return {
          data: {
            data: result.data.data,
            info: { ...result.data.info, nextPageAvailable: false, remainingCount: 0 }
          }
        };
      }
    }),
    publishShopSegment: builder.mutation<ShopSegment, MutateSegmentPayload>({
      invalidatesTags: ['ShopSegment'],
      query: ({ id, version }) => ({
        body: { version },
        method: 'POST',
        url: `/admin/shop/segments/${id}/publish`
      }),
      transformResponse: (response: ApiEnvelope<ShopSegment>) => response.data
    }),
    retryShopSegmentSync: builder.mutation<ShopSegment, RetrySegmentSyncPayload>({
      invalidatesTags: ['ShopSegment'],
      // A live published segment must prove freshness with its version; a
      // stuck delete retry omits it (the backend ignores it on that path).
      query: ({ id, version }) => ({
        body: version === undefined ? {} : { version },
        method: 'POST',
        url: `/admin/shop/segments/${id}/sync/retry`
      }),
      transformResponse: (response: ApiEnvelope<ShopSegment>) => response.data
    }),
    updateShopSegment: builder.mutation<ShopSegment, UpdateSegmentPayload>({
      invalidatesTags: ['ShopSegment'],
      query: ({ id, ...body }) => ({ body, method: 'PATCH', url: `/admin/shop/segments/${id}` }),
      transformResponse: (response: ApiEnvelope<ShopSegment>) => response.data
    })
  })
});

export const {
  useCreateShopSegmentMutation,
  useDeleteShopSegmentMutation,
  useGetShopSegmentAttributesQuery,
  useGetShopSegmentsQuery,
  usePublishShopSegmentMutation,
  useRetryShopSegmentSyncMutation,
  useUpdateShopSegmentMutation
} = shopSegmentsApi;

export { shopSegmentsApi };
